import React, { useRef, useState } from "react";
import { Card, Alert, Button, FormInstance, Row, Col, Tooltip } from "antd";
import { MemberProfile } from "../../../../model/members/MemberProfile";
import { MembershipCategory } from "../../../../model/members/MembershipCategory";
import { QuestionnaireSchema } from "../../forms/QuestionnaireSchema";
import { MemberProfileMode } from "./modeConfig";

import PersonalDetailsForm, { PersonalDetailsFormHandle } from "./PersonalDetailsForm";
import DirectDebitInput from "../DirectDebitInput";
import DynamicQuestionnaire from "../../forms/DynamicQuestionnaire";

interface CreateMemberLayoutProps {
    member: MemberProfile;
    mode: MemberProfileMode;
    // Self-service create (the member onboarding themselves) vs. admin / front-of-
    // house "Create User". Drives the membership-category visibility and whether the
    // health questionnaire is mandatory.
    selfServiceMode: boolean;
    membershipCategories: MembershipCategory[];
    // Save handler — creates the member from the personal-details form values.
    onUpdateMember: (values: Partial<MemberProfile>) => void;
    // Lifts the entered direct-debit mandate up so it can ride along on the
    // member-create request.
    onDirectDebitChange: (values: { accountHolderName: string; bankName: string; accountNumber: string; sortCode: string }) => void;
    directDebitComplete: boolean;
    // Self-service onboarding only: the member has opted to defer direct-debit
    // setup. Greys out the DD fields and drops the DD requirement from Save.
    skipDirectDebit: boolean;
    onSkipDirectDebitChange: (skipped: boolean) => void;
    // The club's signup.directDebitRequired setting (FORT-456): when on, the member
    // can't defer the Direct Debit — the skip option isn't offered at all.
    directDebitRequired: boolean;
    healthSchema: QuestionnaireSchema;
    // The inline health questionnaire is bound to this form. The screen reads it on
    // Save (validated + required for self-service, optional for staff).
    healthForm: FormInstance;
    healthRequired: boolean;
    // A create request is in flight — disables Save to prevent a double-submit.
    creating: boolean;
    // Client-specific direct-debit cancellation terms (empty => none configured).
    // When set and a mandate is being entered, the member must accept them before
    // the member can be created.
    cancellationNotice: string;
    directDebitAgreed: boolean;
    onDirectDebitAgreedChange: (agreed: boolean) => void;
}

// The stripped-down "Create User" page. Unlike the full profile (which shows the
// identity header, summary rail and tabbed billing / activity / health workspace),
// creating a fresh member shows only what's needed to sign them up: personal &
// contact details (incl. emergency contact), a direct-debit mandate, and the
// health questionnaire.
//
// Layout: on desktop, personal details (left) sits beside the direct-debit mandate
// (right) on the same row, with the health questionnaire below — all held to the
// personal-details column width so the questionnaire isn't stretched full-bleed.
// Below the `lg` breakpoint (tablet / phone) every section stacks to full width.
const CreateMemberLayout: React.FC<CreateMemberLayoutProps> = ({
    member,
    mode,
    selfServiceMode,
    membershipCategories,
    onUpdateMember,
    onDirectDebitChange,
    directDebitComplete,
    skipDirectDebit,
    onSkipDirectDebitChange,
    directDebitRequired,
    healthSchema,
    healthForm,
    healthRequired,
    creating,
    cancellationNotice,
    directDebitAgreed,
    onDirectDebitAgreedChange,
}) => {
    // The personal-details form owns the create submit; drive it from the Save
    // buttons rendered below (top actions bar + PARQ footer) via this handle.
    const personalRef = useRef<PersonalDetailsFormHandle>(null);
    // Mirrored Save enablement so both externally-rendered buttons share the
    // disabled state and the missing-fields tooltip.
    const [saveability, setSaveability] = useState<{ canSave: boolean; missingItems: string[] }>({
        canSave: false,
        missingItems: [],
    });

    // A single Save button spec, reused by the top actions bar and the PARQ footer.
    // Both fire the same member-create submit and share the gating/tooltip.
    const renderSaveButton = (testId: string, extra?: { block?: boolean; style?: React.CSSProperties; size?: "small" | "middle" | "large" }) => (
        <Tooltip
            title={saveability.canSave ? undefined : saveability.missingItems.length ? `Complete before saving: ${saveability.missingItems.join(", ")}` : undefined}
        >
            <span data-testid={`${testId}-wrap`} style={extra?.block ? { display: "block" } : undefined}>
                <Button
                    type="primary"
                    size={extra?.size ?? "small"}
                    block={extra?.block}
                    disabled={!saveability.canSave || creating}
                    loading={creating}
                    onClick={() => personalRef.current?.submit()}
                    data-testid={testId}
                    style={extra?.style}
                >
                    Save
                </Button>
            </span>
        </Tooltip>
    );

    return (
    <>
        {/* Top actions bar — the create Save action, lifted out of the personal-
            details card so it applies to the whole new-member form, not just that
            section. */}
        <div
            style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginBottom: 12 }}
            data-testid="create-actions-bar"
        >
            {renderSaveButton("profile-quick-save")}
        </div>

        <Row gutter={[16, 16]} align="top">
        <Col xs={24} lg={12}>
            <PersonalDetailsForm
                ref={personalRef}
                member={member}
                mode={mode}
                membershipCategories={membershipCategories}
                onUpdate={onUpdateMember}
                isCreating
                hideSaveButton
                onSaveabilityChange={setSaveability}
                selfServiceMode={selfServiceMode}
                directDebitComplete={directDebitComplete}
                directDebitSkipped={skipDirectDebit}
                directDebitAgreementRequired={cancellationNotice.trim().length > 0}
                directDebitAgreed={directDebitAgreed}
                creating={creating}
            />
        </Col>

        {/* Right column: the direct-debit mandate with the health questionnaire
            stacked beneath it, so the tall PARQ fills the space next to the
            personal-details column instead of leaving a gap. */}
        <Col xs={24} lg={12}>
            <div className="ft-card-stack">
                {mode.ddMandate && (
                    <DirectDebitInput
                        membershipId={member.membershipId}
                        selfServiceMode={selfServiceMode}
                        canEdit
                        isCreating
                        onCreateChange={onDirectDebitChange}
                        // Self-service onboarding must provide a mandate; for admin/front-of-
                        // house create it's optional, so don't mark the fields required.
                        mandateRequired={selfServiceMode && !skipDirectDebit}
                        // Only the member's own onboarding gets the defer-DD opt-out, and
                        // only at clubs that don't require a Direct Debit (FORT-456).
                        allowSkip={selfServiceMode && !directDebitRequired}
                        skipped={skipDirectDebit}
                        onSkipChange={onSkipDirectDebitChange}
                        cancellationNotice={cancellationNotice}
                        onAgreedChange={onDirectDebitAgreedChange}
                    />
                )}

                <Card title="Health questionnaire" size="small" data-testid="create-health-questionnaire">
                    <Alert
                        type={healthRequired ? "warning" : "info"}
                        showIcon
                        style={{ marginBottom: 16 }}
                        message={
                            healthRequired
                                ? "Please complete the health questionnaire below before saving."
                                : "Complete the member's health questionnaire below. It's optional here — it can also be added later from their profile."
                        }
                    />
                    <DynamicQuestionnaire schema={healthSchema} form={healthForm} />

                    {/* A second Save at the foot of the (tall) PARQ so staff can
                        submit without scrolling back to the top actions bar. Held a
                        bit wider than the top button per the create-page design. */}
                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
                        {renderSaveButton("create-parq-save", { size: "middle", style: { minWidth: 160 } })}
                    </div>
                </Card>
            </div>
        </Col>
        </Row>
    </>
    );
};

export default CreateMemberLayout;
